<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        :title="title"
        :lazy="true"
        @hidden="close"
        @shown="focus"
        id="modal-diagnosis-dialog"
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-form-group label-cols="3" label="Diagnosis:" label-for="diagnosis">
            <vet-select
                ref="diagnosis"
                showMenu
                module="diagnosis"
                placeholder="Diagnosis (required)"
                label="term"
                v-model="diagnosis"
                :class="{
                    'border-danger': formError.includes('diagnosis')
                }"
                :clearOnSelect="false"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Opened:" label-for="opened">
            <date-time-picker
                id="opened"
                v-model="opened"
                v-validate
                :placeholder="{
                    date: 'Opened (required)',
                    time: 'Time (required)'
                }"
                :inner-class="{
                    'border-danger':
                        errors.has('opened') || formError.includes('opened')
                }"
                rules="required"
                name="opened"
                container="#modal-diagnosis-dialog"
                format="datetime"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Opened By:" label-for="opened by">
            <vue-select
                v-model="openedBy"
                label="fullname"
                placeholder="Opened by (required)"
                :disabled="isLoadingListStaff"
                :options="staffOptions"
                :class="{
                    'border-danger': formError.includes('openedBy')
                }"
            >
                <template v-slot:open-indicator>
                    <span></span>
                </template>
            </vue-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Closed:" label-for="closed">
            <date-time-picker
                is-optional
                id="closed"
                v-model="closed"
                v-validate
                :placeholder="{
                    date: `Closed (${
                        isRequiredClosed ? 'required' : 'optional'
                    })`,
                    time: `Time (${isRequiredClosed ? 'required' : 'optional'})`
                }"
                :inner-class="{
                    'border-danger':
                        errors.has('closed') || formError.includes('closed')
                }"
                :rules="isRequiredClosed ? 'required' : ''"
                name="closed"
                container="#modal-diagnosis-dialog"
                format="datetime"
                @update-required="
                    value => {
                        ishasdataclosed = value;
                    }
                "
                :key="closedPickerKey"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Closed By:" label-for="closed by">
            <vue-select
                v-model="closedBy"
                label="fullname"
                :placeholder="
                    isRequiredClosed ? 'Closed by (required)' : 'Closed by'
                "
                :disabled="isLoadingListStaff"
                :options="staffOptions"
                :class="{
                    'border-danger': formError.includes('closedBy')
                }"
            >
                <template v-slot:open-indicator>
                    <span></span>
                </template>
            </vue-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Status:" label-for="status">
            <b-select v-model="status">
                <option value="1">Differential</option>
                <option value="2">Definitive</option>
            </b-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Acuity:" label-for="acuity">
            <b-select v-model="acuity">
                <option value="1">Acute</option>
                <option value="0">Chronic</option>
            </b-select>
        </b-form-group>
        <b-form-group
            v-if="isShowSoapField"
            label-cols="3"
            label="Medical Note:"
            label-for="medical note"
        >
            <b-select v-model="medicalNote" :options="medicalNoteOptions" />
        </b-form-group>

        <b-form-group label-cols="3" label="Notes:" label-for="notes">
            <editable
                id="notes"
                placeholder="Notes (optional)"
                v-model="notes"
            />
        </b-form-group>

        <template v-slot:modal-footer>
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button-group v-if="!isSaving && !isEditMode">
                <b-dropdown
                    v-if="!continues"
                    @click="save(false)"
                    left
                    split
                    text="Save + Done"
                    variant="primary"
                >
                    <b-dropdown-item @click="save"
                        >Save + Add New</b-dropdown-item
                    >
                </b-dropdown>
                <b-dropdown
                    v-else
                    @click="save(true)"
                    left
                    split
                    text="Save + New"
                    variant="primary"
                >
                    <b-dropdown-item @click="save(false)">
                        Save + Done
                    </b-dropdown-item>
                </b-dropdown>
            </b-button-group>

            <b-button
                variant="primary"
                @click="save(false)"
                v-if="!isSaving && isEditMode"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import VueSelect from "vue-select";
import VetSelect from "@/module/commons/vet-select";
import { DateTime } from "luxon";
import Loader from "@/module/commons/loader";
import DateTimePicker from "@/module/commons/date-time-picker.vue";
import { sortBy, isEmpty } from "lodash";
import Editable from "@/components/elements/editable-textarea";

export default {
    name: "ModalDiagnosis",
    components: {
        VetSelect,
        VueSelect,
        Loader,
        DateTimePicker,
        Editable
    },
    props: {
        patientId: {
            type: String
        },
        soapId: {
            // Used in soap page
            type: String,
            default: null
        }
    },
    data() {
        return {
            title: "",
            diagnosis: null,
            opened: null,
            openedBy: null,
            closed: null,
            closedBy: null,
            status: "1",
            acuity: "1",
            medicalNote: "",
            medicalNoteOptions: [],
            notes: "",
            formError: [],
            timeFormat: "hh:mm A",
            isLoadingListStaff: true,
            staffOptions: [],
            isSaving: false,
            isEditMode: false,
            continues: false,
            editRecordId: null,
            closedPickerKey: 0,
            ishasdataclosed: false,
            isRecordClosed: false
        };
    },
    computed: {
        staffPlaceholder() {
            return this.isLoadingListStaff
                ? "Loading..."
                : "Select staff member (optional)";
        },
        isRequiredClosed() {
            return (
                this.ishasdataclosed ||
                !isEmpty(this.closedBy) ||
                this.isRecordClosed
            );
        },
        isSoap() {
            return this.$route.name === "soap";
        },
        isShowSoapField() {
            return !this.isEditMode && !this.isSoap;
        }
    },
    watch: {
        diagnosis(value) {
            if (value) {
                this.clearError("diagnosis");
            }
        },
        openedBy(value) {
            if (value) {
                this.clearError("openedBy");
            }
        }
    },
    methods: {
        open(params = {}) {
            let title = "New Diagnosis";

            if (params.action === "edit") {
                title = "Edit Diagnosis";
                this.isEditMode = true;
                this.applyRecord(params.editRecord);
                this.fetchStaffList(false);
            } else {
                this.medicalNote = this.soapId ? this.soapId : "";
                this.initDateTime();
                this.fetchStaffList();
            }

            this.$refs.dialog.show();
            this.title = title;
            this.fetchMedicalNotes();
        },
        focus() {
            this.$refs.diagnosis.focus();
        },
        close() {
            this.$refs.dialog.hide();
        },
        applyRecord(record = {}) {
            if (record.date_closed && record.date_closed !== "") {
                this.closed = this._toDateTime(record.date_closed);
            }

            this.editRecordId = record.id;
            this.opened = this._toDateTime(record.date_opened);
            this.status = record.status.toString();
            this.acuity = record.is_acute.toString();
            this.notes = record.note;
            this.medicalNote = record.soap_id;

            this.diagnosis = {
                description_id: record.description_id,
                id: record.description_id,
                type: record.description_type,
                term: record.description
            };

            if (record.opened_by) {
                record.opened_by.fullname = record.opened_by.full_name;
                this.openedBy = record.opened_by;
            }

            if (record.closed_by) {
                record.closed_by.fullname = record.closed_by.full_name;
                this.closedBy = record.closed_by;
            }

            if (record.date_closed) {
                this.ishasdataclosed = true;
                this.isRecordClosed = true;
            }
        },
        clearError(key) {
            this.formError = this.formError.filter(err => err !== key);
        },
        async save(reset = false) {
            try {
                this.formError = [];

                if (!this.diagnosis) {
                    this.formError.push("diagnosis");
                }

                if (!this.opened) {
                    this.formError.push("opened");
                }

                if (!this.openedBy) {
                    this.formError.push("openedBy");
                }

                if (
                    (!this.closed && this.isRequiredClosed) ||
                    (this.closed && this.closed < this.opened)
                ) {
                    this.formError.push("closed");
                }

                this.closedBy = !Array.isArray(this.closedBy)
                    ? this.closedBy
                    : this.closedBy.length > 0
                    ? this.closedBy
                    : null;

                if (!this.closedBy && this.isRequiredClosed) {
                    this.formError.push("closedBy");
                }

                if (this.formError.length > 0) {
                    return;
                }

                let openedDate = "";
                let openedTime = "";
                let closedDate = "";
                let closedTime = "";
                let closed_by_id = this.closedBy ? this.closedBy.id : "";
                let closed_by = this.closedBy ? this.closedBy.fullname : "";
                let diagnosis_id = this.isEditMode ? this.editRecordId : "";

                if (this.opened) {
                    openedDate = this.opened.toFormat(this._dateFormat);
                    openedTime = this.opened.toFormat("hh:mm a");
                }

                if (this.closed) {
                    closedDate = this.closed.toFormat(this._dateFormat);
                    closedTime = this.closed.toFormat("hh:mm a");
                }

                const fd = new FormData();
                fd.append("patient_id", this.patientId);
                fd.append("soap_id", this.medicalNote);
                fd.append("description_id", this.diagnosis.description_id);
                fd.append("description_type", this.diagnosis.type);
                fd.append("opened_by_id", this.openedBy.id);
                fd.append("description", this.diagnosis.term);
                fd.append("date_opened", openedDate);
                fd.append("time_opened", openedTime);
                fd.append("opened_by", this.openedBy.fullname);
                fd.append("status", this.status);
                fd.append("is_acute", this.acuity);
                fd.append("note", this.notes);
                fd.append("closed_by_id", closed_by_id);
                fd.append("closed_by", closed_by);
                fd.append("date_closed", closedDate);
                fd.append("time_closed", closedTime);
                fd.append("diagnosis_id", diagnosis_id);

                this.isSaving = true;

                const res = await this.$http.post(
                    `${process.env.CI_APPS}common/diagnosis/saveDiagnosis`,
                    fd
                );

                if (res.data.state === 0) {
                    this.isSaving = false;
                    this._message(res.data.message);
                    this.$emit("onsuccess");
                    this.continues = reset;
                    if (reset) {
                        this.diagnosis = null;
                        this.opened = null;
                        this.openedBy = null;
                        this.closed = null;
                        this.closedBy = null;
                        this.status = "1";
                        this.acuity = "1";
                        this.medicalNote = this.soapId ? this.soapId : "";
                        this.notes = "";
                        this.formError = [];
                        this.isEditMode = false;
                        this.editRecordId = null;
                        this.closedPickerKey++;
                        this.ishasdataclosed = false;
                        this.initDateTime();
                        this.fetchStaffList();
                        this.$refs.diagnosis.clear();
                        this.focus();
                    } else {
                        this.close();
                    }
                } else {
                    throw new Error(res.data.message);
                }
            } catch (e) {
                this.isSaving = false;
                this._message(e.message, "danger");
            }
        },
        initDateTime() {
            this.opened = DateTime.local().setZone(this._timeZone);
        },
        async fetchMedicalNotes() {
            const defaultItem = {
                value: "",
                text: "No Medical Note"
            };

            try {
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}record/medical-note/${this.patientId}`
                );

                const response = res.data.response;

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    let options = [];
                    let dateFormat = this._getLuxonFormat();
                    response.resources = sortBy(response.resources, ["date"]);

                    for (const rawRecord of response.resources) {
                        let label = rawRecord.title || "Medical Note";
                        if (
                            isEmpty(rawRecord.title) &&
                            !isEmpty(rawRecord.template)
                        ) {
                            label = rawRecord.template.label;
                        }

                        label += ` (${this._toDateTime(rawRecord.date).toFormat(dateFormat)})`; // prettier-ignore

                        options.push({
                            value: rawRecord.id,
                            text: label
                        });
                    }

                    options = options.reverse();

                    this.medicalNoteOptions = [defaultItem, ...options];
                } else {
                    throw new Error("Unable to fetch medical notes");
                }
            } catch (e) {
                this.medicalNoteOptions = [defaultItem];
                console.log(e);
            }
        },
        async fetchStaffList(isSetDefault = true) {
            try {
                this.isLoadingListStaff = true;
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}actor/staff`
                );

                const response = res.data.response;

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    this.staffOptions = response.resources || [];
                    this.isLoadingListStaff = false;

                    if (isSetDefault) {
                        this.openedBy = this.$root.currentActor;
                    }

                    // set default (for edit)
                    /* if (this.editMode && defaultStaffId) {
                        let staff = response.resources.find(
                            rec => rec.id === defaultStaffId
                        );

                        if (staff) {
                            this.enrolledBy = staff;
                        }
                    } */
                } else {
                    throw new Error("Unable to get staff list");
                }
            } catch (e) {
                this.isLoadingListStaff = false;
                this.staffOptions = [];
            }
        }
    }
};
</script>
